import { Box, Button, Flex, Icon, Input, Popover, PopoverContent, PopoverTrigger, Text, useColorModeValue, useDisclosure } from "@chakra-ui/react"
import { useMemo, useRef, useState } from "react"
import { MdArrowDropDown, MdCheck } from "react-icons/md"
import { tokenFilter } from "lib/utils"
import { pinChosen, sortByLabel } from "lib/sort"

// At most this many options are drawn; the rest is reached through the search field
const MAX_SHOWN = 200

// Drop-down for ONE choice, for the forms: a search field on top and the options sorted by
// name (Serbian alphabet, "Group 9" before "Group 10").
// - options:  [{ value, label, hint? }]  sorted and searched; `hint` is a small grey text
// - pinned:   [{ value, label }]  always on top, not sorted, not searched ("Create new ...")
// - value:    the chosen value ("" or empty = nothing chosen)
// - onChange: called with the new value, "" when the choice is cleared
// - placeholder: the "nothing chosen" text; clicking it while something is chosen clears
//   the choice, like the empty option of a plain <select>
// The list opens inside the drawer (no Portal): a Chakra Modal keeps focus and scrolling
// inside itself, so a list drawn outside of it could not be typed into or scrolled.
export default function SearchSelect({ options, pinned, value, onChange, placeholder = "Select...", isDisabled }) {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [term, setTerm] = useState("")
  const searchRef = useRef(null)

  const border = useColorModeValue("gray.200", "whiteAlpha.300")
  const borderHover = useColorModeValue("gray.300", "whiteAlpha.400")
  const textColor = useColorModeValue("gray.800", "whiteAlpha.900")
  const mutedColor = useColorModeValue("gray.500", "gray.400")
  const popoverBg = useColorModeValue("white", "navy.800")
  const hoverBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100")
  const selectedBg = useColorModeValue("brand.50", "whiteAlpha.200")

  const pinnedOptions = pinned || []
  // Sorted only while the list is open, so a closed drop-down costs nothing when the form
  // around it is redrawn (e.g. on every key typed in another field)
  const sorted = useMemo(() => isOpen ? sortByLabel(options || []) : [], [isOpen, options])
  // With an empty search the chosen option is the first one of the list (it is also shown ticked)
  const matching = useMemo(() => {
    const found = sorted.filter(option => tokenFilter(option.label, term))
    return term.trim() || !value ? found : pinChosen(found, option => option.value === value)
  }, [sorted, term, value])
  const shown = matching.slice(0, MAX_SHOWN)

  const selected = [...pinnedOptions, ...(options || [])].find(option => !!value && option.value === value)

  const close = () => {
    onClose()
    setTerm("")
  }
  const choose = (next) => {
    onChange(next)
    close()
  }

  const renderOption = (option, key) => {
    const isSelected = !!selected && option.value === selected.value
    return (
      <Flex
        key={key}
        as="button"
        type="button"
        role="option"
        aria-selected={isSelected}
        w="100%"
        align="center"
        textAlign="left"
        gap={2}
        px={2}
        py={1.5}
        borderRadius="md"
        bg={isSelected ? selectedBg : "transparent"}
        _hover={{ bg: hoverBg }}
        onClick={() => choose(option.value)}
      >
        <Text as="span" flex="1" minW={0} fontSize="sm" color={textColor} noOfLines={2}>{option.label || "(no name)"}</Text>
        {option.hint && <Text as="span" fontSize="xs" color={mutedColor} flexShrink={0}>{option.hint}</Text>}
        {isSelected && <Icon as={MdCheck} color="brand.500" flexShrink={0} />}
      </Flex>
    )
  }

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={close}
      initialFocusRef={searchRef}
      isLazy
      matchWidth
      gutter={4}
      placement="bottom-start"
      strategy="fixed"
    >
      <PopoverTrigger>
        <Button
          type="button"
          variant="outline"
          w="100%"
          h={10}
          px={4}
          fontWeight="normal"
          borderRadius="md"
          borderColor={border}
          boxShadow="none"
          justifyContent="space-between"
          isDisabled={isDisabled}
          color={selected ? textColor : mutedColor}
          _hover={{ borderColor: borderHover }}
          rightIcon={<Icon as={MdArrowDropDown} w={5} h={5} />}
        >
          <Text as="span" isTruncated>{selected ? (selected.label || "(no name)") : placeholder}</Text>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        w="100%"
        maxW="92vw"
        bg={popoverBg}
        _focus={{ boxShadow: "none" }}
        rootProps={{ zIndex: "popover" }}
        // Escape closes only this list; it must not reach the drawer and close the whole form
        onKeyDown={e => { if (e.key === "Escape") e.stopPropagation() }}
      >
        <Box p={2}>
          <Input
            ref={searchRef}
            size="sm"
            borderRadius="md"
            placeholder="Search..."
            value={term}
            onChange={e => setTerm(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault()
                if (shown[0]) choose(shown[0].value)
              }
            }}
          />
          <Box role="listbox" maxH="260px" overflowY="auto" mt={2}>
            {selected && (
              <Flex as="button" type="button" w="100%" px={2} py={1.5} borderRadius="md" _hover={{ bg: hoverBg }} onClick={() => choose("")}>
                <Text as="span" fontSize="sm" color={mutedColor}>{placeholder}</Text>
              </Flex>
            )}
            {pinnedOptions.map(option => renderOption(option, "pinned-" + option.value))}
            {pinnedOptions.length > 0 && <Box borderBottom="1px solid" borderColor={border} my={1} />}
            {shown.map(option => renderOption(option, option.value))}
            {matching.length === 0 && <Text fontSize="sm" color={mutedColor} px={2} py={2}>Nothing found</Text>}
            {matching.length > shown.length && (
              <Text fontSize="xs" color={mutedColor} px={2} py={2}>
                {matching.length - shown.length} more, type to narrow the list
              </Text>
            )}
          </Box>
        </Box>
      </PopoverContent>
    </Popover>
  )
}